<template>
  <footer class="relative bg-zinc-900 text-white pt-16 pb-8 overflow-hidden border-t-4 border-amber-600" :dir="dir">
    
    <div class="absolute inset-0 opacity-5 pointer-events-none" 
         style="background-image: radial-gradient(#B38E60 1px, transparent 1px); background-size: 32px 32px;">
    </div>

    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
        
        <div class="lg:col-span-4 space-y-6">
          <div class="flex items-center gap-3">
            <div class="bg-white p-2 rounded-md inline-block">
                <img src="../../assets/logosham.svg" class="h-12 w-auto" alt="Logo" />
            </div>
          </div>
          
          <p class="text-zinc-400 leading-relaxed text-sm max-w-sm">
            {{ t('hero.description') }}
          </p>

          <div class="flex items-center gap-4 pt-2">
            <SocialMediaComponent /> 
            <a href="https://wa.me/+966504125583" target="_blank" 
               class="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:bg-[#25D366] hover:text-white transition-all duration-300">
              <font-awesome-icon icon="fa-brands fa-whatsapp" class="text-xl" />
            </a>
          </div>
        </div>

        <div class="lg:col-span-2">
          <h3 class="text-sm font-bold uppercase tracking-widest text-amber-500 mb-6">
            {{ t('nav.home') }}
          </h3>
          <ul class="space-y-4 text-sm">
            <li>
              <RouterLink to="/service" class="text-zinc-400 hover:text-white transition-colors hover:pl-2 duration-300 block">
                {{ t('nav.services') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/about" class="text-zinc-400 hover:text-white transition-colors hover:pl-2 duration-300 block">
                {{ t('nav.about') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/projects" class="text-zinc-400 hover:text-white transition-colors hover:pl-2 duration-300 block">
                {{ t('nav.projects') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/contact" class="text-zinc-400 hover:text-white transition-colors hover:pl-2 duration-300 block">
                {{ t('nav.contact') }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <div class="lg:col-span-3">
          <h3 class="text-sm font-bold uppercase tracking-widest text-amber-500 mb-6">
            {{ t('contactUs.title') }}
          </h3>
          <ul class="space-y-5 text-sm">
            <li>
              <a href="mailto:admin@adcce.com" class="group flex items-start gap-3">
                <div class="mt-1 text-amber-600 group-hover:text-amber-400 transition-colors">
                  <font-awesome-icon icon="fa-solid fa-envelope" />
                </div>
                <span class="text-zinc-400 group-hover:text-white transition-colors">admin@adcce.com</span>
              </a>
            </li>
            <li>
              <a href="tel:+966540290354" class="group flex items-start gap-3">
                <div class="mt-1 text-amber-600 group-hover:text-amber-400 transition-colors">
                  <font-awesome-icon icon="fa-solid fa-phone" />
                </div>
                <span class="text-zinc-400 group-hover:text-white transition-colors" 
                      :style="{ direction: 'ltr', unicodeBidi: 'plaintext', textAlign: dir === 'rtl' ? 'right' : 'left' }">
                  {{ t('contactUs.phone') }}
                </span>
              </a>
            </li>
            <li>
              <a href="https://www.google.com/maps/place/Saqer+Al-Jazirah+Royal+Air+Force+Museum/@24.754061,46.739111,16z/data=!4m6!3m5!1s0x3e2f038b30b55497:0xd8cba005ba57bd97!8m2!3d24.7540614!4d46.7391115!16s%2Fm%2F09rtb0t?hl=en&entry=ttu&g_ep=EgoyMDI1MTExNy4wIKXMDSoASAFQAw%3D%3D" target="_blank" class="group flex items-start gap-3">
                <div class="mt-1 text-amber-600 group-hover:text-amber-400 transition-colors">
                  <font-awesome-icon icon="fa-solid fa-location-dot" />
                </div>
                <span class="text-zinc-400 group-hover:text-white transition-colors">
                  {{ t('contactUs.location') }}
                </span>
              </a>
            </li>
          </ul>
        </div>

        <div class="lg:col-span-3">
            <div class="h-48 w-full rounded-lg overflow-hidden border border-zinc-700 shadow-lg hover:border-amber-600/50 transition-colors duration-300 grayscale hover:grayscale-0">
                <GoogleMapsWidget class="w-full h-full" />
            </div>
        </div>

      </div>

      <div class="border-t border-zinc-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-zinc-500">
        <p>{{ t('footer.copyright') }}</p>
        
        <div class="flex items-center gap-2">
          <span>{{ t('footer.powered') }}</span>
          <a href="https://www.linkedin.com/in/khaled-zaattor-3ba321277/" target="_blank" class="text-amber-600 hover:text-amber-400 font-bold uppercase tracking-wider transition-colors">
            {{ t('footer.plateau') }}
          </a>
        </div>
      </div>

    </div>
  </footer>
</template>

<script setup>
    import { useI18n } from 'vue-i18n'
    import { computed } from 'vue'
    import SocialMediaComponent from '@/components/SocialMediaComponent.vue'
    import GoogleMapsWidget from '../GoogleMapsWidget.vue'
    import { useInstallPromptStore } from '@/stores/useInstallPromptStore.js'

    const { t, locale } = useI18n()
    const installPromptStore = useInstallPromptStore()

    const dir = computed(() => (locale.value === 'ar' ? 'rtl' : 'ltr'))

    function handleInstallClick() {
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
            alert("To install this app on your iPhone:\n\n1. Open in Safari.\n2. Tap the Share button.\n3. Select 'Add to Home Screen'.");
        } else if (installPromptStore.deferredPrompt) {
            installPromptStore.deferredPrompt.prompt();
            installPromptStore.deferredPrompt.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('Installation accepted');
                } else {
                    console.log('Installation dismissed');
                }
                installPromptStore.clearDeferredPrompt();
            });
        } else {
            console.log('No installation prompt available');
        }
    }
</script>

<style scoped>
/* Custom Scrollbar for the page if needed, though Tailwind handles most styling now */
</style>